<template>
  <div>
    <div class="section-hero-subpage bg-color-octrary">
      <video autoplay loop muted playsinline width="640" preload="metadata" poster="/frontend/assets/images/og-image.jpg">
        <source src="/frontend/assets/videos/video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div class="container">
        <div class="paragraph--normal text-uppercase text-color-quinary mb-2">
          <span class="notranslate" translate="no">SELECTED</span> CARS
        </div>
        <h1 class="heading--0 text-uppercase text-color-quinary border-bottom-quinary padding-bottom-2 margin-bottom-3-lg">
          Car Export <span class="fw-light">to</span><br />
          {{ destination.name }}
        </h1>
        <div class="row">
          <div class="col-lg-7">
            <p class="paragraph--xlarge text-color-senary">
              {{ destination.heroText }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Intro Section -->
    <div class="section-small bg-color-tertiary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Export European Cars to {{ destination.name }}
        </h2>
        <div class="row">
          <div class="col-lg-10">
            <p class="paragraph--large text-color-senary mb-4" v-for="(p, i) in destination.introParagraphs" :key="i">
              {{ p }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Popular Brands for this destination -->
    <div class="section-small bg-color-primary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Most Popular Brands in {{ destination.name }}
        </h2>
        <div class="row">
          <div v-for="brand in destination.popularBrands" :key="brand" class="col-lg-3 col-md-4 col-6 mb-4">
            <router-link :to="`/brands/${encodeURIComponent(brand.toLowerCase())}`" class="popular-brand">
              <span>{{ brand }}</span>
              <span class="arrow-icon">&rarr;</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works -->
    <div class="section-small bg-color-tertiary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          How Car Export to {{ destination.name }} Works
        </h2>
        <div class="row">
          <div v-for="(step, i) in destination.steps" :key="i" class="col-lg-3 col-md-6 mb-lg-0 mb-4">
            <div class="step-card">
              <div class="step-card--number">{{ String(i + 1).padStart(2, '0') }}</div>
              <div class="step-card--title">{{ step.title }}</div>
              <p class="step-card--text">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Cars -->
    <div class="section-small bg-color-primary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Cars Available for Export to {{ destination.name }}
        </h2>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <template v-else-if="cars.length > 0">
          <CarItem v-for="car in cars.slice(0, 12)" :key="car.id" :car="car" />
          <div class="text-center mt-4" v-if="cars.length > 12">
            <router-link to="/inventory" class="btn btn--normal btn--primary text-uppercase">
              View All {{ cars.length }} Cars
            </router-link>
          </div>
        </template>

        <div v-else class="text-center py-5">
          <p class="paragraph--large text-color-senary">
            Contact us for the latest available inventory for export to {{ destination.name }}.
          </p>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="section-small bg-color-quaternary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3 text-center">
          Frequently Asked Questions
        </h2>
        <div class="faq-list">
          <div v-for="(faq, i) in destination.faqs" :key="i" class="faq-item" @click="toggleFaq(i)">
            <div class="faq-item--question">
              <span>{{ faq.q }}</span>
              <span class="faq-toggle">{{ openFaq === i ? '&minus;' : '&plus;' }}</span>
            </div>
            <div class="faq-item--answer" v-show="openFaq === i">
              {{ faq.a }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="section-small bg-color-primary text-center">
      <div class="container">
        <h2 class="heading--3 text-color-quinary text-uppercase mb-4">
          Ready to Export Your Car to {{ destination.name }}?
        </h2>
        <p class="paragraph--xlarge text-color-senary mb-5">
          Contact us today for a free quote. We handle everything from sourcing to delivery.
        </p>
        <div class="d-flex justify-content-center gap-3 flex-wrap">
          <a href="https://wa.me/421949006016" target="_blank" class="btn btn--normal btn--primary text-uppercase">
            WhatsApp Us
          </a>
          <router-link to="/contacts" class="btn btn--normal btn--primary text-uppercase">
            Contact Form
          </router-link>
          <router-link to="/inventory" class="btn btn--normal btn--primary text-uppercase">
            Browse Cars
          </router-link>
        </div>
      </div>
    </div>

    <AppMarquee />
  </div>
</template>

<script>
import AppMarquee from '../components/AppMarquee.vue';
import CarItem from '../components/CarItem.vue';
import axios from 'axios';

/* ─── Country database ─── */
const countryDB = {
  // ── Europe ──
  'albania': { name: 'Albania', cities: ['Tirana', 'Durr\u00ebs', 'Vlor\u00eb'] },
  'andorra': { name: 'Andorra', cities: ['Andorra la Vella', 'Escaldes-Engordany'] },
  'austria': { name: 'Austria', cities: ['Vienna', 'Salzburg', 'Graz', 'Innsbruck'] },
  'belgium': { name: 'Belgium', cities: ['Brussels', 'Antwerp', 'Ghent', 'Bruges'] },
  'bosnia-and-herzegovina': { name: 'Bosnia and Herzegovina', cities: ['Sarajevo', 'Banja Luka', 'Mostar'] },
  'bulgaria': { name: 'Bulgaria', cities: ['Sofia', 'Plovdiv', 'Varna', 'Burgas'] },
  'croatia': { name: 'Croatia', cities: ['Zagreb', 'Split', 'Dubrovnik', 'Rijeka'] },
  'cyprus': { name: 'Cyprus', cities: ['Nicosia', 'Limassol', 'Larnaca', 'Paphos'] },
  'czech-republic': { name: 'Czech Republic', cities: ['Prague', 'Brno', 'Ostrava', 'Plze\u0148'] },
  'denmark': { name: 'Denmark', cities: ['Copenhagen', 'Aarhus', 'Odense', 'Aalborg'] },
  'estonia': { name: 'Estonia', cities: ['Tallinn', 'Tartu', 'Narva'] },
  'finland': { name: 'Finland', cities: ['Helsinki', 'Espoo', 'Tampere', 'Turku'] },
  'france': { name: 'France', cities: ['Paris', 'Lyon', 'Marseille', 'Nice', 'Toulouse'] },
  'germany': { name: 'Germany', cities: ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Stuttgart'] },
  'greece': { name: 'Greece', cities: ['Athens', 'Thessaloniki', 'Heraklion', 'Patras'] },
  'hungary': { name: 'Hungary', cities: ['Budapest', 'Debrecen', 'Szeged', 'Miskolc'] },
  'iceland': { name: 'Iceland', cities: ['Reykjavik', 'K\u00f3pavogur', 'Akureyri'] },
  'ireland': { name: 'Ireland', cities: ['Dublin', 'Cork', 'Galway', 'Limerick'] },
  'italy': { name: 'Italy', cities: ['Rome', 'Milan', 'Naples', 'Turin', 'Florence'] },
  'kosovo': { name: 'Kosovo', cities: ['Pristina', 'Prizren', 'Peja'] },
  'latvia': { name: 'Latvia', cities: ['Riga', 'Daugavpils', 'Liep\u0101ja'] },
  'liechtenstein': { name: 'Liechtenstein', cities: ['Vaduz', 'Schaan'] },
  'lithuania': { name: 'Lithuania', cities: ['Vilnius', 'Kaunas', 'Klaip\u0117da'] },
  'luxembourg': { name: 'Luxembourg', cities: ['Luxembourg City', 'Esch-sur-Alzette'] },
  'malta': { name: 'Malta', cities: ['Valletta', 'Birkirkara', 'Sliema'] },
  'moldova': { name: 'Moldova', cities: ['Chi\u0219in\u0103u', 'B\u0103l\u021bi', 'Tiraspol'] },
  'monaco': { name: 'Monaco', cities: ['Monte Carlo', 'La Condamine'] },
  'montenegro': { name: 'Montenegro', cities: ['Podgorica', 'Budva', 'Kotor'] },
  'netherlands': { name: 'Netherlands', cities: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht'] },
  'north-macedonia': { name: 'North Macedonia', cities: ['Skopje', 'Bitola', 'Ohrid'] },
  'norway': { name: 'Norway', cities: ['Oslo', 'Bergen', 'Trondheim', 'Stavanger'] },
  'poland': { name: 'Poland', cities: ['Warsaw', 'Krak\u00f3w', 'Wroc\u0142aw', 'Gda\u0144sk', 'Pozna\u0144'] },
  'portugal': { name: 'Portugal', cities: ['Lisbon', 'Porto', 'Faro', 'Braga'] },
  'romania': { name: 'Romania', cities: ['Bucharest', 'Cluj-Napoca', 'Timi\u0219oara', 'Ia\u0219i'] },
  'san-marino': { name: 'San Marino', cities: ['San Marino City', 'Serravalle'] },
  'serbia': { name: 'Serbia', cities: ['Belgrade', 'Novi Sad', 'Ni\u0161'] },
  'slovenia': { name: 'Slovenia', cities: ['Ljubljana', 'Maribor', 'Celje'] },
  'spain': { name: 'Spain', cities: ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Malaga'] },
  'sweden': { name: 'Sweden', cities: ['Stockholm', 'Gothenburg', 'Malm\u00f6', 'Uppsala'] },
  'switzerland': { name: 'Switzerland', cities: ['Zurich', 'Geneva', 'Basel', 'Bern'] },
  'ukraine': { name: 'Ukraine', cities: ['Kyiv', 'Odesa', 'Lviv', 'Kharkiv'] },
  'united-kingdom': { name: 'United Kingdom', cities: ['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Glasgow'] },
  'vatican-city': { name: 'Vatican City', cities: ['Vatican City'] },
  // ── Middle East ──
  'uae': { name: 'UAE', fullName: 'United Arab Emirates', cities: ['Dubai', 'Abu Dhabi', 'Sharjah'] },
  'saudi-arabia': { name: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah', 'Dammam'] },
  'qatar': { name: 'Qatar', cities: ['Doha', 'Al Wakrah', 'Al Khor'] },
  'kuwait': { name: 'Kuwait', cities: ['Kuwait City', 'Hawalli', 'Salmiya'] },
  'bahrain': { name: 'Bahrain', cities: ['Manama', 'Muharraq', 'Riffa'] },
  'oman': { name: 'Oman', cities: ['Muscat', 'Salalah', 'Sohar'] },
  // ── Asia ──
  'china': { name: 'China', cities: ['Shanghai', 'Beijing', 'Guangzhou', 'Shenzhen', 'Tianjin'] },
  'japan': { name: 'Japan', cities: ['Tokyo', 'Osaka', 'Yokohama', 'Nagoya'] },
  'south-korea': { name: 'South Korea', cities: ['Seoul', 'Busan', 'Incheon', 'Daegu'] },
  'singapore': { name: 'Singapore', cities: ['Singapore'] },
  'hong-kong': { name: 'Hong Kong', cities: ['Hong Kong', 'Kowloon'] },
  'thailand': { name: 'Thailand', cities: ['Bangkok', 'Phuket', 'Chiang Mai', 'Pattaya'] },
  'indonesia': { name: 'Indonesia', cities: ['Jakarta', 'Surabaya', 'Bali', 'Bandung'] },
  'malaysia': { name: 'Malaysia', cities: ['Kuala Lumpur', 'Penang', 'Johor Bahru'] },
  'vietnam': { name: 'Vietnam', cities: ['Ho Chi Minh City', 'Hanoi', 'Da Nang'] },
  'philippines': { name: 'Philippines', cities: ['Manila', 'Cebu', 'Davao'] },
  'kazakhstan': { name: 'Kazakhstan', cities: ['Almaty', 'Astana', 'Shymkent'] },
  // ── Americas ──
  'united-states': { name: 'United States', cities: ['New York', 'Los Angeles', 'Miami', 'Chicago', 'Houston'] },
  'canada': { name: 'Canada', cities: ['Toronto', 'Vancouver', 'Montreal', 'Calgary'] },
  // ── Oceania ──
  'australia': { name: 'Australia', cities: ['Sydney', 'Melbourne', 'Brisbane', 'Perth'] },
  'new-zealand': { name: 'New Zealand', cities: ['Auckland', 'Wellington', 'Christchurch'] },
  // ── Africa ──
  'africa': { name: 'Africa', fullName: 'African Countries', cities: ['Lagos', 'Nairobi', 'Johannesburg', 'Accra', 'Dar es Salaam'] },
};

/* ─── Custom content overrides for key markets ─── */
const customContent = {
  'uae': {
    heroText: 'Export premium European cars to Dubai, Abu Dhabi, Sharjah and across the UAE. Competitive European pricing, full documentation and door-to-door delivery.',
    introParagraphs: [
      'The United Arab Emirates is one of our largest export markets. Clients in Dubai, Abu Dhabi and Sharjah regularly source luxury vehicles from Europe through Selected Cars, taking advantage of competitive European pricing.',
      'We handle the complete export process: sourcing from authorized European dealers, EU export documentation, shipping, customs coordination and final delivery to your location anywhere in the Emirates.',
      'European-spec vehicles are highly sought after in the UAE market. Whether you are looking for a Mercedes G-Class, BMW X7, Porsche Cayenne or Ferrari, we source it at the best European price.'
    ],
    popularBrands: ['Mercedes-Benz', 'BMW', 'Porsche', 'Land Rover', 'Ferrari', 'Lamborghini', 'Bentley', 'Rolls-Royce'],
    faqs: [
      { q: 'How much can I save buying a car from Europe vs the local market?', a: 'Savings vary by brand and model, but clients typically benefit from competitive European pricing, especially on brands like Porsche, BMW and Mercedes-Benz where European market competition drives favorable prices.' },
      { q: 'What documents do I need to import a car to UAE?', a: 'You will typically need: Emirates ID, passport copy, vehicle invoice, EU export certificate, bill of lading, and insurance. Selected Cars prepares all export documents from the European side.' },
      { q: 'Can you source any car model?', a: 'Yes! We have access to 92 partner dealerships across Europe. If the model exists in the European market, we can source it. This includes special editions, limited runs and custom configurations.' },
      { q: 'Do European-spec cars work in UAE?', a: 'Yes, European-spec vehicles are fully compatible with UAE roads. The speedometer shows km/h (which is what UAE uses). All safety features, AC systems and electronics work perfectly in the Gulf climate.' }
    ]
  },
  'saudi-arabia': {
    heroText: 'Export luxury cars from Europe to Riyadh, Jeddah, Dammam and across Saudi Arabia. Competitive prices from European dealerships with complete export service.',
    introParagraphs: [
      'Saudi Arabia is a key market for European luxury car exports. With Vision 2030 driving economic growth, demand for premium vehicles continues to rise. Selected Cars helps Saudi buyers access European-spec vehicles at competitive prices.',
      'We provide full-service car export from Europe to Saudi Arabia: sourcing from authorized European dealers, export documentation, compliance guidance, shipping and customs support.',
      'Our Saudi clients regularly source Mercedes S-Class, BMW 7 Series, Porsche Cayenne, Land Rover Range Rover and supercars from Ferrari and Lamborghini at competitive European prices.'
    ],
    popularBrands: ['Mercedes-Benz', 'BMW', 'Land Rover', 'Porsche', 'Audi', 'Ferrari', 'Lamborghini', 'Bentley'],
    faqs: [
      { q: 'How much can I save importing from Europe to Saudi Arabia?', a: 'Clients benefit from competitive European pricing, particularly on luxury brands. European market competition drives favorable prices for many models compared to regional pricing.' },
      { q: 'What are the requirements for importing a car to Saudi Arabia?', a: 'Requirements include meeting local standards, vehicle inspection, and proper documentation. You need: national ID/Iqama, invoice, export certificate, bill of lading, and customs declaration. We guide you through the process.' },
      { q: 'Do you handle the complete process?', a: 'We handle everything on the European side: sourcing, purchasing, documentation and shipping. We also provide guidance and support for the import procedures on the Saudi side.' },
      { q: 'What brands are most popular with Saudi buyers?', a: 'Mercedes-Benz, BMW, Land Rover and Porsche are the most popular choices. We also export significant numbers of Ferrari, Lamborghini and Bentley supercars and luxury vehicles.' }
    ]
  },
  'qatar': {
    heroText: 'Export premium European vehicles to Doha and across Qatar. Source luxury cars from Europe at competitive prices with full export service.',
    introParagraphs: [
      'Qatar is a premium market for European luxury vehicles. Clients in Doha source high-end vehicles from Europe through Selected Cars, benefiting from competitive European pricing.',
      'We manage the entire export process: vehicle sourcing from authorized European dealers, EU export paperwork, shipping to Qatar, and coordination with local customs clearance.',
      'Popular models exported to Qatar include Mercedes G-Class, Porsche 911 and Cayenne, BMW X series, Ferrari and Lamborghini supercars, and Range Rover vehicles.'
    ],
    popularBrands: ['Mercedes-Benz', 'Porsche', 'BMW', 'Land Rover', 'Ferrari', 'Lamborghini', 'Bentley', 'Rolls-Royce'],
    faqs: [
      { q: 'What is the price advantage of buying from Europe?', a: 'European pricing is typically very competitive compared to regional market rates, with the advantage varying by brand and model. Contact us for a specific quote on any vehicle.' },
      { q: 'Can I import any car model to Qatar?', a: 'We can source any model available in the European market. We recommend contacting us with your specific requirements so we can advise on availability and pricing.' },
      { q: 'How does the process work?', a: 'Simply tell us what car you want. We source it from our European dealer network, handle all export documentation, arrange shipping to Qatar, and coordinate with customs clearance.' },
      { q: 'How do I register a European-spec car in Qatar?', a: 'After customs clearance, you take the vehicle to the Traffic Department for inspection and registration. European-spec vehicles are fully compatible with Qatar requirements.' }
    ]
  },
  'germany': {
    heroText: 'Buy premium vehicles within Germany at the best dealer prices. We source from our network of 92 European partner dealerships.',
    introParagraphs: [
      'Even within Germany, finding the best price on a luxury vehicle can be challenging. Selected Cars leverages our network of 92 partner dealerships across Europe \u2014 including German dealers \u2014 to find the absolute best price for your desired vehicle.',
      'We handle the sourcing, negotiation and delivery within Germany. Whether you are looking for a specific configuration, a rare model or simply the best market price, our team does the work for you.',
      'German clients benefit from our pan-European sourcing network. Sometimes the best price on a German car is actually at a dealer in another EU country \u2014 we find those deals and handle the EU-internal transfer seamlessly.'
    ],
    popularBrands: ['BMW', 'Mercedes-Benz', 'Porsche', 'Audi', 'Volkswagen', 'Ferrari', 'Lamborghini', 'Land Rover'],
    steps: [
      { title: 'Tell Us What You Want', text: 'Specify brand, model, configuration. We search our 92-dealer network for the best price.' },
      { title: 'We Find the Deal', text: 'We present options with transparent pricing. You choose the best offer.' },
      { title: 'Purchase & Docs', text: 'We handle the purchase, EU paperwork and transfer documentation.' },
      { title: 'Delivery', text: 'Covered transport delivery to your address anywhere in Germany.' }
    ],
    faqs: [
      { q: 'Why use Selected Cars if I am already in Germany?', a: 'Our 92-dealer network across Europe means we often find better prices than you would get walking into a single dealership. We do the legwork of comparing prices across multiple countries and dealers.' },
      { q: 'Are there any issues buying from a dealer in another EU country?', a: 'No. The EU single market means free movement of goods. We handle all cross-border paperwork and you receive full EU documentation for German registration.' },
      { q: 'What brands do you source most for German clients?', a: 'BMW, Mercedes-Benz, Porsche and Audi are the most popular. But we source any brand available in Europe, including Italian supercars and British luxury vehicles.' }
    ]
  },
  'china': {
    heroText: 'Export European luxury vehicles to Shanghai, Beijing, Guangzhou, Shenzhen and across China. Competitive European pricing on premium brands.',
    introParagraphs: [
      'China is a major market for European luxury vehicles. Selected Cars provides Chinese buyers and importers access to the full range of European-spec vehicles at competitive prices.',
      'We handle European-side logistics: vehicle sourcing, EU export documentation, and shipping to major Chinese ports. Our clients include individual buyers, import companies and dealer groups.',
      'European-spec vehicles are popular with Chinese buyers who value authentic European quality and configuration. We source the latest models with full European specifications.'
    ],
    popularBrands: ['Mercedes-Benz', 'BMW', 'Porsche', 'Land Rover', 'Ferrari', 'Lamborghini', 'Bentley', 'Audi'],
    faqs: [
      { q: 'What is the price advantage for Chinese buyers?', a: 'European pricing is often very competitive compared to Chinese domestic prices. Contact us with specific models for detailed pricing comparisons.' },
      { q: 'Which Chinese ports do you ship to?', a: 'We ship to all major Chinese ports: Shanghai, Tianjin, Guangzhou, Shenzhen, Dalian and others. Shanghai and Tianjin are the most popular for vehicle imports.' },
      { q: 'Do you work with Chinese import companies?', a: 'Yes, we work with individual buyers, parallel import dealers and import companies across China. We provide competitive pricing for volume orders.' },
      { q: 'What documentation do you provide?', a: 'We provide complete EU export documentation, vehicle invoices, certificates of conformity, and all paperwork needed for the Chinese import process.' }
    ]
  },
  'africa': {
    heroText: 'Export European cars to Africa \u2014 Nigeria, Kenya, South Africa, Ghana, Tanzania and more. Competitive prices with full shipping service.',
    introParagraphs: [
      'Africa is a growing market for European luxury vehicles. Selected Cars exports premium vehicles to clients across the African continent, including Nigeria, Kenya, South Africa, Ghana, Tanzania, Ethiopia and many more countries.',
      'We provide complete export service from Europe to African ports: vehicle sourcing, EU export documentation, and shipping via RoRo or container to major African ports.',
      'Our African clients source a wide range of vehicles from luxury SUVs like Range Rover and Mercedes GLS to performance cars from BMW and Porsche. European pricing offers competitive advantages.'
    ],
    popularBrands: ['Mercedes-Benz', 'BMW', 'Land Rover', 'Toyota', 'Porsche', 'Audi', 'Volkswagen', 'Ferrari'],
    faqs: [
      { q: 'Which African countries do you export to?', a: 'We export to all African countries including Nigeria, Kenya, South Africa, Ghana, Tanzania, Ethiopia, Uganda, Rwanda, Senegal, Ivory Coast, Cameroon, DR Congo and more.' },
      { q: 'What shipping options are available?', a: 'We offer RoRo (Roll-on/Roll-off) which is more affordable, and container shipping which provides full protection. Container is recommended for luxury and high-value vehicles.' },
      { q: 'How do I pay for the vehicle?', a: 'We accept bank transfers in EUR. Payment is typically split: deposit to reserve, balance before shipping. We provide full invoicing for customs purposes.' },
      { q: 'Can you help with local import requirements?', a: 'We provide complete documentation from the European side. Import rules vary by country \u2014 we can advise on general requirements and connect you with local customs agents.' }
    ]
  }
};

/* ─── Generate destination from country data ─── */
function generateDestination(slug) {
  const c = countryDB[slug];
  if (!c) return null;

  const name = c.name;
  const cityList = c.cities.slice(0, 3).join(', ');
  const custom = customContent[slug] || {};

  const base = {
    name: name,
    heroText: `Export premium European cars to ${cityList} and across ${name}. Competitive European pricing with full export service.`,
    introParagraphs: [
      `${name} is a valued market for European luxury vehicles. Selected Cars helps buyers in ${cityList} and across ${name} access premium European-spec vehicles at competitive prices from our network of 92 partner dealerships.`,
      `We provide complete export service from Europe to ${name}: vehicle sourcing from authorized European dealers, EU export documentation, shipping and delivery coordination. Our experienced team handles every detail of the process.`
    ],
    popularBrands: ['Mercedes-Benz', 'BMW', 'Porsche', 'Audi', 'Land Rover', 'Ferrari', 'Lamborghini', 'Volkswagen'],
    steps: [
      { title: 'Choose Your Car', text: `Browse our inventory or tell us what you need. We source any model from our 92 European partner dealerships.` },
      { title: 'Reserve & Pay', text: 'Secure your vehicle with a deposit. We handle all EU export paperwork and documentation.' },
      { title: 'Shipping', text: `Your car is shipped from Europe to ${name}. We handle all logistics and provide tracking updates.` },
      { title: 'Delivery', text: `We coordinate delivery to ${c.cities[0]} or anywhere in ${name}. Full documentation provided.` }
    ],
    faqs: [
      { q: `How do I buy a car from Europe and ship it to ${name}?`, a: `Simply browse our inventory or tell us what car you are looking for. We source it from our European dealer network, handle all export documentation, and arrange shipping to ${name}. We manage the entire process from start to finish.` },
      { q: `What brands can you source for export to ${name}?`, a: 'We source all major European brands including Mercedes-Benz, BMW, Porsche, Audi, Ferrari, Lamborghini, Land Rover, Bentley, Rolls-Royce, Volkswagen and more. Any brand available in the European market can be sourced.' },
      { q: 'How does the payment process work?', a: 'Payment is typically split into a deposit to reserve the vehicle and the balance before shipping. We accept bank transfers in EUR and provide full invoicing and documentation for your records.' },
      { q: `Can you deliver to any city in ${name}?`, a: `Yes, we coordinate delivery to ${c.cities.join(', ')} and all other locations in ${name}. We handle logistics from our European base to your preferred destination.` }
    ]
  };

  // Merge custom overrides
  return {
    ...base,
    ...custom,
    // Keep base steps if no custom steps
    steps: custom.steps || base.steps
  };
}

export default {
  components: { AppMarquee, CarItem },
  data() {
    return {
      cars: [],
      loading: true,
      openFaq: null
    };
  },
  computed: {
    countrySlug() {
      return this.$route.params.country || '';
    },
    destination() {
      const generated = generateDestination(this.countrySlug);
      if (generated) return generated;

      // Fallback for unknown slugs
      const name = this.countrySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      return {
        name: name,
        heroText: 'Export premium European cars worldwide. Competitive prices, full documentation and door-to-door delivery.',
        introParagraphs: ['Selected Cars exports premium European vehicles worldwide. Contact us for pricing and shipping details to your destination.'],
        popularBrands: ['Mercedes-Benz', 'BMW', 'Porsche', 'Land Rover', 'Audi', 'Ferrari'],
        steps: [
          { title: 'Choose Car', text: 'Browse inventory or request specific model.' },
          { title: 'Reserve', text: 'Deposit to secure. We handle EU export docs.' },
          { title: 'Ship', text: 'We arrange shipping to your destination.' },
          { title: 'Receive', text: 'Door-to-door delivery worldwide.' }
        ],
        faqs: [
          { q: 'Do you export to my country?', a: 'Yes, we export worldwide. Contact us with your location and desired vehicle for a custom quote including shipping.' }
        ]
      };
    }
  },
  methods: {
    toggleFaq(i) {
      this.openFaq = this.openFaq === i ? null : i;
    },
    parseVehicleFields(v) {
      const brand = (v.make || '').trim();
      let model = (v.model || '').trim();
      let trimLine = (v.trim_line || '').trim();
      if (model.includes('*')) {
        const parts = model.split('*').map(p => p.trim()).filter(Boolean);
        let firstPart = parts[0];
        if (brand && firstPart.toLowerCase().startsWith(brand.toLowerCase())) {
          firstPart = firstPart.substring(brand.length).trim();
        }
        model = firstPart || parts[0];
        trimLine = parts.slice(1).join(', ');
      }
      if (trimLine.includes('*')) {
        const parts = trimLine.split('*').map(p => p.trim()).filter(Boolean);
        let startIdx = 0;
        if (parts[0] && brand) {
          const first = parts[0].toLowerCase();
          if (first.startsWith(brand.toLowerCase())) startIdx = 1;
        }
        trimLine = parts.slice(startIdx).join(', ');
      }
      return { model, trimLine };
    },
    mapVehicle(v) {
      const parsed = this.parseVehicleFields(v);
      return {
        id: v.id,
        brand: v.make,
        model: parsed.model,
        year: v.year,
        mileage: v.mileage,
        nettoPrice: v.netto_price,
        grossPrice: v.gross_price,
        trimLine: parsed.trimLine,
        image: v.primary_image ? v.primary_image.url : null,
        status: v.status === 'active' ? 'Available' : v.status === 'sold' ? 'Sold' : v.status
      };
    }
  },
  async mounted() {
    try {
      const res = await axios.get('/api/vehicles');
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || []);
      this.cars = raw.filter(v => v.status === 'active').map(this.mapVehicle);
    } catch (e) {
      console.error('Error loading cars:', e);
    } finally {
      this.loading = false;
    }

    const d = this.destination;
    import('../seo.js').then(({ setPageSeo }) => setPageSeo({
      title: `Car Export to ${d.name} | Buy European Cars for ${d.name} | Selected Cars`,
      description: `Export luxury European cars to ${d.name}. BMW, Mercedes, Porsche, Ferrari \u2014 competitive European prices with full export service. Trusted European car exporter since 2004.`,
      keywords: `car export to ${d.name.toLowerCase()}, buy car from europe ship to ${d.name.toLowerCase()}, european car export ${d.name.toLowerCase()}, luxury car import ${d.name.toLowerCase()}, BMW export ${d.name.toLowerCase()}, Mercedes export ${d.name.toLowerCase()}, car shipping europe to ${d.name.toLowerCase()}, selected cars ${d.name.toLowerCase()}`,
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: 'Export Destinations', url: '/export' },
        { name: d.name, url: `/export/${this.countrySlug}` }
      ],
      schema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": d.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      }
    }));
  },
  watch: {
    '$route.params.country'() {
      this.openFaq = null;
      this.$forceUpdate();
      window.scrollTo(0, 0);

      // Re-run SEO
      const d = this.destination;
      import('../seo.js').then(({ setPageSeo }) => setPageSeo({
        title: `Car Export to ${d.name} | Buy European Cars for ${d.name} | Selected Cars`,
        description: `Export luxury European cars to ${d.name}. BMW, Mercedes, Porsche, Ferrari \u2014 competitive European prices with full export service.`,
        keywords: `car export to ${d.name.toLowerCase()}, buy car from europe ${d.name.toLowerCase()}, european car export ${d.name.toLowerCase()}`,
        breadcrumbs: [
          { name: 'Home', url: '/' },
          { name: 'Export Destinations', url: '/export' },
          { name: d.name, url: `/export/${this.countrySlug}` }
        ]
      }));
    }
  }
};
</script>

<style scoped>
.popular-brand {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: #222;
  border: 1px solid rgba(255,255,255,0.12);
  text-decoration: none;
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.85rem;
  transition: border-color 0.3s, background 0.3s;
}
.popular-brand:hover {
  background: #2a2a2a;
  border-color: rgba(255,255,255,0.3);
}
.arrow-icon {
  opacity: 0.4;
  transition: opacity 0.3s;
}
.popular-brand:hover .arrow-icon { opacity: 1; }

.step-card {
  padding: 1.5rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  height: 100%;
}
.step-card--number {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255,255,255,0.1);
  line-height: 1;
  margin-bottom: 0.75rem;
}
.step-card--title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}
.step-card--text {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.5);
  line-height: 1.5;
  margin: 0;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}
.faq-item {
  border-bottom: 1px solid rgba(255,255,255,0.08);
  cursor: pointer;
}
.faq-item--question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 0;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  gap: 1rem;
}
.faq-toggle {
  font-size: 1.5rem;
  color: rgba(255,255,255,0.4);
  flex-shrink: 0;
}
.faq-item--answer {
  padding: 0 0 1.25rem 0;
  color: rgba(255,255,255,0.6);
  font-size: 0.9rem;
  line-height: 1.6;
}
</style>
